import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The `<source>` children of a deferred `<video>`, in the ONE shape the video engine understands.
 *
 * Every section that can hold an uploaded clip emitted this by hand, identically, which is exactly how the
 * `data-width` below would have been forgotten in three of them. It carries the rendition's pixel width so
 * `promoteVideo` can pick the file that fits the box the clip is actually painted in — a phone has no
 * business downloading the 1280 copy of a band it renders 390 px wide.
 *
 * `data-src` rather than `src` is the deferred contract: nothing is fetched until the engine promotes it.
 * Width is omitted when unknown (clips uploaded before renditions existed), and the engine treats a source
 * with no width as "use it if there is nothing better" — so old documents keep working untouched.
 *
 * `data-height` RIDES ALONG FOR THE CROP (2026-09-18). Every section paints its clip with
 * `object-fit: cover`, and a cover crop does NOT consume the box's width — it consumes whatever the
 * SHORTER axis forces. A 16/9 clip in a square 300 px tile is scaled until its height covers 300, which
 * makes it 533 px wide: the engine was asking for a rendition covering 300 and painting one across 533.
 * Measured in real Chromium on a multicolumn card at builder width: the 640 rung promoted into a crop that
 * needs 1067 device px — a 1.67x upscale of the SOFTEST copy on the shelf, which is the founder's
 * "original quality, and it pixelates" report (18/09). The height is what lets `neededWidth` know the
 * clip's aspect BEFORE a byte is fetched; without it the engine only learns it on `loadedmetadata`, i.e.
 * after it has already downloaded the wrong file.
 */
/**
 * The `<source>` list of a PRODUCT clip (21/09/2026) — a gallery entry rather than a section's
 * `VideoValue`. The product carries its ladder as `sources` (see `StorefrontVideoSource`); a clip stored
 * before the ladder existed, or served by an older backend, carries none and plays from its one url,
 * exactly as it did before. The MIME type is read off the file name: the asset service stores `.mp4`
 * and `.webm` and nothing else, and a source with the wrong type is one the browser refuses outright.
 */
export function clipSources(item) {
    const rungs = item.sources && item.sources.length > 0 ? item.sources : [{ url: item.url }];
    return rungs
        .filter((r) => typeof r.url === 'string' && r.url.length > 0)
        .map((r) => ({
        url: r.url,
        type: videoTypeOf(r.url),
        ...(typeof r.width === 'number' && r.width > 0 ? { width: r.width } : null),
        ...(typeof r.height === 'number' && r.height > 0 ? { height: r.height } : null),
    }));
}
function videoTypeOf(url) {
    const path = url.split(/[?#]/, 1)[0] ?? url;
    return /\.webm$/i.test(path) ? 'video/webm' : 'video/mp4';
}
export function videoSourceTags(sources) {
    return sources.map((sv) => (_jsx("source", { "data-src": sv.url, type: sv.type, ...(sv.width ? { 'data-width': sv.width } : null), ...(sv.height ? { 'data-height': sv.height } : null) }, sv.url)));
}
//# sourceMappingURL=videoSources.js.map